import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  AlertTriangle,
  BarChart3,
  Clock3,
  Download,
  FileText,
  Headphones,
  Play,
  PhoneCall,
  RefreshCw,
  Users,
} from "lucide-react";
import {
  api,
  apiErrToStr,
  fmtDate,
  fmtDuration,
  translateAbandonmentCause,
  translateHangupCause,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";

const initialData = {
  summary: {},
  comparison: {},
  period: {},
  hourly: [],
  queues: [],
  agents: [],
  calls: [],
  received_calls: [],
  placed_calls: [],
  missed_calls: [],
  abandoned_calls: [],
  received_summary: {},
  placed_summary: {},
  direction_summary: {},
  abandonment_ranges: [],
  recording_agents: [],
  agent_state_summary: {},
  disconnect_causes: [],
  abandonment_causes: [],
  ivr: [],
  recordings: [],
};

const statusOptions = [
  { value: "", label: "Todos os status" },
  { value: "completed", label: "Completadas" },
  { value: "not_completed", label: "Nao completadas" },
];

const directionOptions = [
  { value: "", label: "Todos os tipos" },
  { value: "inbound", label: "Entrada externa" },
  { value: "outbound", label: "Saida externa" },
  { value: "local", label: "Interna" },
];

function MetricCard({ label, value, detail, icon: Icon, tone = "blue" }) {
  const tones = {
    blue: "text-[color:var(--accent-blue)]",
    green: "text-[color:var(--accent-green)]",
    amber: "text-[color:var(--accent-amber)]",
    red: "text-[color:var(--accent-red)]",
  };
  return (
    <div className="sbc-card p-4 flex items-center gap-3">
      <div className={`w-10 h-10 flex items-center justify-center bg-[color:var(--bg-elevated)] ${tones[tone]}`}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">{label}</div>
        <div className={`text-2xl font-mono font-semibold ${tones[tone]}`}>{value}</div>
        <div className="text-[11px] text-[color:var(--text-muted)]">{detail}</div>
      </div>
    </div>
  );
}

function CausePill({ label, value, tone = "neutral" }) {
  const toneClass = {
    neutral: "bg-[color:var(--bg-elevated)] text-[color:var(--text-primary)]",
    red: "bg-[color:var(--tag-danger-bg)] text-[color:var(--accent-red)]",
    amber: "bg-[color:var(--tag-warning-bg)] text-[color:var(--accent-amber)]",
  }[tone];
  return (
    <div className={`px-3 py-2 text-xs rounded-sm ${toneClass}`}>
      <div className="font-semibold">{label}</div>
      <div className="text-[11px] opacity-80">{value} chamada(s)</div>
    </div>
  );
}

function SectionCard({ title, children, helper }) {
  return (
    <div className="sbc-card overflow-hidden">
      <div className="p-4 border-b border-[color:var(--border-default)]">
        <h2 className="text-sm font-semibold">{title}</h2>
        {helper ? <div className="text-[11px] text-[color:var(--text-muted)] mt-1">{helper}</div> : null}
      </div>
      <div className="p-0">{children}</div>
    </div>
  );
}

function renderDirectionLabel(direction) {
  const normalized = String(direction || "").trim().toLowerCase();
  if (normalized === "inbound") return "Entrada externa";
  if (normalized === "outbound") return "Saida externa";
  if (normalized === "local") return "Interna";
  return normalized || "-";
}

function RecordingActions({ row }) {
  if (!row?.cdr_id) return row?.recording_path ? "Disponivel" : "-";
  const audioUrl = `${api.defaults.baseURL}/recordings/library/${row.cdr_id}/audio`;
  const downloadUrl = `${audioUrl}?download=true`;
  return (
    <div className="flex items-center gap-2">
      <audio controls preload="none" className="h-8 max-w-[220px]">
        <source src={audioUrl} />
      </audio>
      <a className="sbc-btn h-8 px-2" href={audioUrl} target="_blank" rel="noreferrer" title="Ouvir">
        <Play size={14} />
      </a>
      <a className="sbc-btn h-8 px-2" href={downloadUrl} title="Baixar">
        <Download size={14} />
      </a>
    </div>
  );
}

const REPORT_SECTIONS = [
  { id: "overview", label: "Visao geral", to: "/call-center/relatorios/visao-geral" },
  { id: "queues", label: "Filas", to: "/call-center/relatorios/filas" },
  { id: "agents", label: "Agentes", to: "/call-center/relatorios/agentes" },
  { id: "calls", label: "Chamadas", to: "/call-center/relatorios/chamadas" },
  { id: "received", label: "Recebidas", to: "/call-center/relatorios/recebidas" },
  { id: "placed", label: "Realizadas", to: "/call-center/relatorios/realizadas" },
  { id: "missed", label: "Perdidas", to: "/call-center/relatorios/perdidas" },
  { id: "abandoned", label: "Abandonadas", to: "/call-center/relatorios/abandonadas" },
  { id: "ivr", label: "URA", to: "/call-center/relatorios/ura" },
  { id: "recordings", label: "Gravacoes", to: "/call-center/relatorios/gravacoes" },
];

export default function CallCenterReports({ section = "overview" }) {
  const { activeTenantId } = useAuth();
  const [filters, setFilters] = useState({
    days: 30,
    start: "",
    end: "",
    queue: "",
    agent: "",
    src: "",
    dst: "",
    direction: "",
    status: "",
    service_level_seconds: 20,
  });
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [queues, setQueues] = useState([]);
  const [agents, setAgents] = useState([]);

  const updateFilter = (key, value) => setFilters((old) => ({ ...old, [key]: value }));
  const reportParams = () => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== "" && value != null),
    );
    if (activeTenantId) params.tenant_id = activeTenantId;
    return params;
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/callcenter/reports", { params: reportParams() });
      setData({ ...initialData, ...(response.data || {}) });
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || "Falha ao carregar os relatorios.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [activeTenantId]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [queueResponse, agentResponse] = await Promise.all([
          api.get("/filas"),
          api.get("/callcenter-agents"),
        ]);
        setQueues(
          (queueResponse.data || [])
            .filter((row) => row?.enabled !== false)
            .sort((a, b) => String(a?.numero || "").localeCompare(String(b?.numero || ""), "pt-BR", { numeric: true })),
        );
        setAgents(
          (agentResponse.data || [])
            .filter((row) => row?.enabled !== false)
            .sort((a, b) => String(a?.agente || "").localeCompare(String(b?.agente || ""), "pt-BR", { numeric: true })),
        );
      } catch (_err) {
        setQueues([]);
        setAgents([]);
      }
    };
    loadOptions();
  }, [activeTenantId]);

  const summary = data.summary || {};
  const topHours = useMemo(
    () => (data.hourly || []).filter((row) => Number(row.offered || 0) > 0),
    [data.hourly],
  );
  const maxHour = Math.max(1, ...topHours.map((row) => Number(row.offered || 0)));

  const exportReport = async (format) => {
    const response = await api.get(`/callcenter/reports/export.${format}`, {
      params: reportParams(),
      responseType: "blob",
    });
    const disposition = response.headers?.["content-disposition"] || "";
    const serverName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
    const link = document.createElement("a");
    link.href = URL.createObjectURL(response.data);
    link.download = serverName || `callcenter-relatorios.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  const comparison = data.comparison || {};
  const stateSummary = data.agent_state_summary || {};

  const deltaLabel = (item, inverse = false) => {
    const percent = Number(item?.delta_percent || 0);
    const positive = inverse ? percent <= 0 : percent >= 0;
    const signal = percent > 0 ? "+" : "";
    return {
      text: `${signal}${percent.toFixed(2)}% vs periodo anterior`,
      className: positive ? "text-[color:var(--accent-green)]" : "text-[color:var(--accent-red)]",
    };
  };

  return (
    <div className="space-y-5" data-testid="callcenter-reports-page">
      <nav className="flex gap-1 overflow-x-auto border-b border-[color:var(--border-default)] pb-2" aria-label="Relatorios do call center">
        {REPORT_SECTIONS.map((item) => (
          <NavLink key={item.id} to={item.to} className={({ isActive }) => `sbc-btn whitespace-nowrap ${isActive ? "sbc-btn-primary" : ""}`}>
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">
            Historico detalhado
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">Relatorios do call center</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1">
            Dashboard operacional com chamadas, filas, agentes, SLA, URA e gravacoes usando dados reais.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="sbc-btn" onClick={() => exportReport("xlsx")}>
            <Download size={14} /> Excel
          </button>
          <button className="sbc-btn" onClick={() => exportReport("pdf")}>
            <FileText size={14} /> PDF
          </button>
          <button className="sbc-btn" onClick={() => exportReport("csv")}>
            <FileText size={14} /> CSV
          </button>
          <button className="sbc-btn sbc-btn-primary" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
        </div>
      </div>

      <div className="sbc-card p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <label className="text-xs">
            Periodo
            <select
              className="sbc-input block mt-1"
              value={filters.days}
              onChange={(e) => updateFilter("days", Number(e.target.value))}
            >
              <option value="1">Hoje</option>
              <option value="7">Ultimos 7 dias</option>
              <option value="30">Ultimos 30 dias</option>
              <option value="90">Ultimos 90 dias</option>
              <option value="180">Ultimos 180 dias</option>
              <option value="365">Ultimo ano</option>
            </select>
          </label>
          <label className="text-xs">
            Inicio
            <input
              className="sbc-input block mt-1"
              type="date"
              value={filters.start}
              onChange={(e) => updateFilter("start", e.target.value)}
            />
          </label>
          <label className="text-xs">
            Fim
            <input
              className="sbc-input block mt-1"
              type="date"
              value={filters.end}
              onChange={(e) => updateFilter("end", e.target.value)}
            />
          </label>
          <label className="text-xs">
            Fila
            <select
              className="sbc-input block mt-1 font-mono"
              value={filters.queue}
              onChange={(e) => updateFilter("queue", e.target.value)}
            >
              <option value="">Todas as filas</option>
              {queues.map((row) => (
                <option key={row.id || row.numero} value={row.numero}>
                  {row.numero} - {row.nome || "Fila"}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <label className="text-xs">
            Agente
            <select
              className="sbc-input block mt-1 font-mono"
              value={filters.agent}
              onChange={(e) => updateFilter("agent", e.target.value)}
            >
              <option value="">Todos os agentes</option>
              {agents.map((row) => (
                <option key={row.id || row.agente} value={row.agente}>
                  {row.agente} - {row.nome || "Agente"}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            SLA em segundos
            <input
              className="sbc-input block mt-1"
              type="number"
              min="5"
              max="300"
              value={filters.service_level_seconds}
              onChange={(e) => updateFilter("service_level_seconds", Number(e.target.value))}
            />
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <label className="text-xs">
            Origem
            <input
              className="sbc-input block mt-1 font-mono"
              value={filters.src}
              onChange={(e) => updateFilter("src", e.target.value)}
              placeholder="Numero, DID ou ramal"
            />
          </label>
          <label className="text-xs">
            Destino
            <input
              className="sbc-input block mt-1 font-mono"
              value={filters.dst}
              onChange={(e) => updateFilter("dst", e.target.value)}
              placeholder="Fila, ramal ou destino"
            />
          </label>
          <label className="text-xs">
            Tipo
            <select
              className="sbc-input block mt-1"
              value={filters.direction}
              onChange={(e) => updateFilter("direction", e.target.value)}
            >
              {directionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs">
            Resultado
            <select
              className="sbc-input block mt-1"
              value={filters.status}
              onChange={(e) => updateFilter("status", e.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex justify-end">
          <button className="sbc-btn sbc-btn-primary" onClick={load} disabled={loading}>
            <BarChart3 size={14} /> Aplicar filtros
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-[color:var(--accent-red)]">{error}</div>}

      {section === "overview" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
            <MetricCard label="Recebidas" value={summary.offered || 0} detail={deltaLabel(comparison.offered).text} icon={PhoneCall} tone="blue" />
            <MetricCard label="Atendidas" value={summary.answered || 0} detail={deltaLabel(comparison.answered).text} icon={Headphones} tone="green" />
            <MetricCard label="Abandonadas" value={summary.abandoned || 0} detail={deltaLabel(comparison.abandoned, true).text} icon={AlertTriangle} tone="red" />
            <MetricCard label="SLA" value={`${summary.service_level || 0}%`} detail={deltaLabel(comparison.service_level).text} icon={BarChart3} tone="amber" />
            <MetricCard label="TME" value={fmtDuration(summary.avg_wait_sec || 0)} detail={deltaLabel(comparison.avg_wait_sec, true).text} icon={Clock3} tone="blue" />
            <MetricCard label="TMA" value={fmtDuration(summary.avg_talk_sec || 0)} detail={deltaLabel(comparison.avg_talk_sec).text} icon={Users} tone="green" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4">
            <div className="sbc-card p-4">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 size={16} />
                <h2 className="text-sm font-semibold">Volume por hora</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
                {topHours.length ? topHours.map((row) => (
                  <div key={row.hour} className="rounded-sm border border-[color:var(--border-default)] p-3">
                    <div className="text-[10px] uppercase text-[color:var(--text-muted)]">{row.hour}</div>
                    <div className="mt-2 h-24 flex items-end">
                      <div
                        className="w-full bg-[color:var(--accent-blue)]/85 rounded-t-sm"
                        style={{ height: `${Math.max(10, (Number(row.offered || 0) / maxHour) * 100)}%` }}
                      />
                    </div>
                    <div className="mt-2 text-xs text-[color:var(--text-secondary)]">
                      {row.offered || 0} recebidas / {row.answered || 0} atendidas
                    </div>
                  </div>
                )) : (
                  <div className="text-sm text-[color:var(--text-muted)] col-span-full">Sem volume no periodo.</div>
                )}
              </div>
            </div>

            <div className="sbc-card p-4 space-y-4">
              <div>
                <div className="text-sm font-semibold mb-3">Motivos de encerramento</div>
                <div className="grid grid-cols-1 gap-2">
                  {(data.disconnect_causes || []).length ? data.disconnect_causes.map((cause) => (
                    <CausePill key={cause.name} label={translateHangupCause(cause.name)} value={cause.value} />
                  )) : <div className="text-sm text-[color:var(--text-muted)]">Sem eventos no periodo.</div>}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold mb-3">Motivos de abandono</div>
                <div className="grid grid-cols-1 gap-2">
                  {(data.abandonment_causes || []).length ? data.abandonment_causes.map((cause) => (
                    <CausePill key={cause.name} label={translateAbandonmentCause(cause.name)} value={cause.value} tone="amber" />
                  )) : <div className="text-sm text-[color:var(--text-muted)]">Sem abandonos no periodo.</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr] gap-4">
            <div className="sbc-card p-4">
              <div className="text-sm font-semibold mb-3">Estados dos agentes no periodo</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-sm border border-[color:var(--border-default)] p-3"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Disponivel</div><div className="text-xl font-mono mt-1">{stateSummary.available || 0}</div></div>
                <div className="rounded-sm border border-[color:var(--border-default)] p-3"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Em chamada</div><div className="text-xl font-mono mt-1">{stateSummary.busy || 0}</div></div>
                <div className="rounded-sm border border-[color:var(--border-default)] p-3"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Em pausa</div><div className="text-xl font-mono mt-1">{stateSummary.on_break || 0}</div></div>
                <div className="rounded-sm border border-[color:var(--border-default)] p-3"><div className="text-[10px] uppercase text-[color:var(--text-muted)]">Offline</div><div className="text-xl font-mono mt-1">{stateSummary.offline || 0}</div></div>
              </div>
            </div>
            <div className="sbc-card p-4">
              <div className="text-sm font-semibold mb-3">Janela analisada</div>
              <div className="space-y-2 text-xs text-[color:var(--text-secondary)]">
                <div><span className="font-semibold">Atual:</span> {fmtDate(data.period?.current_start)} ate {fmtDate(data.period?.current_end)}</div>
                <div><span className="font-semibold">Comparacao:</span> {fmtDate(data.period?.previous_start)} ate {fmtDate(data.period?.previous_end)}</div>
                <div><span className="font-semibold">Filtro:</span> {filters.queue ? `Fila ${filters.queue}` : "Todas as filas"} / {filters.agent ? `Agente ${filters.agent}` : "Todos os agentes"}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {section === "queues" && <div className="sbc-card overflow-hidden">
        <div className="p-4 border-b border-[color:var(--border-default)]">
          <h2 className="text-sm font-semibold">Desempenho por fila</h2>
        </div>
        <table className="sbc-table">
          <thead>
            <tr>
              <th>Fila</th>
              <th>Recebidas</th>
              <th>Atendidas</th>
              <th>Abandonadas</th>
              <th>SLA</th>
              <th>Espera media</th>
              <th>Maior espera</th>
              <th>Transferencias</th>
            </tr>
          </thead>
          <tbody>
            {(data.queues || []).length ? data.queues.map((row) => (
              <tr key={row.queue}>
                <td className="font-mono">{row.queue}</td>
                <td>{row.offered || 0}</td>
                <td>{row.answered || 0}</td>
                <td>{row.abandoned || 0}</td>
                <td>{row.service_level || 0}%</td>
                <td>{fmtDuration(row.avg_wait_sec || 0)}</td>
                <td>{fmtDuration(row.max_wait_sec || 0)}</td>
                <td>{row.transfers || 0}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="8" className="text-center text-[color:var(--text-muted)]">Sem chamadas por fila no periodo.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>}

      {section === "agents" && <div className="sbc-card overflow-hidden">
        <div className="p-4 border-b border-[color:var(--border-default)]">
          <h2 className="text-sm font-semibold">Produtividade por agente</h2>
        </div>
        <table className="sbc-table">
          <thead>
            <tr>
              <th>Agente</th>
              <th>Atendidas</th>
              <th>Perdidas</th>
              <th>Taxa de atendimento</th>
              <th>Tempo falado</th>
              <th>TMA</th>
              <th>Primeira atividade</th>
              <th>Ultima atividade</th>
            </tr>
          </thead>
          <tbody>
            {(data.agents || []).length ? data.agents.map((row) => (
              <tr key={row.agent}>
                <td className="font-mono">{row.agent}</td>
                <td>{row.answered || 0}</td>
                <td>{row.missed || 0}</td>
                <td>{row.answer_rate || 0}%</td>
                <td>{fmtDuration(row.total_talk_sec || 0)}</td>
                <td>{fmtDuration(row.avg_talk_sec || 0)}</td>
                <td>{fmtDate(row.first_activity)}</td>
                <td>{fmtDate(row.last_activity)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="8" className="text-center text-[color:var(--text-muted)]">Sem agentes no periodo.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>}

      {section === "calls" && <div className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-4">
        <div className="sbc-card overflow-hidden">
          <div className="p-4 border-b border-[color:var(--border-default)]">
            <h2 className="text-sm font-semibold">Chamadas abandonadas e nao completadas</h2>
          </div>
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Origem</th>
                <th>Fila</th>
                <th>Agente</th>
                <th>Espera</th>
                <th>Resultado</th>
                <th>Encerramento</th>
              </tr>
            </thead>
            <tbody>
              {(data.abandoned_calls || []).length ? data.abandoned_calls.map((row) => (
                <tr key={`${row.call_id}-${row.started_at}`}>
                  <td>{fmtDate(row.started_at)}</td>
                  <td className="font-mono">{row.src || "-"}</td>
                  <td>{row.queue_name || row.queue_number || "-"}</td>
                  <td>{row.agent || "-"}</td>
                  <td>{fmtDuration(row.wait_sec || 0)}</td>
                  <td>{row.disposition || "-"}</td>
                  <td>{translateHangupCause(row.hangup_cause)}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="text-center text-[color:var(--text-muted)]">Nenhuma chamada abandonada no periodo.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="sbc-card overflow-hidden">
          <div className="p-4 border-b border-[color:var(--border-default)]">
            <h2 className="text-sm font-semibold">URA / navegacao</h2>
          </div>
          <table className="sbc-table">
            <thead>
              <tr>
                <th>URA</th>
                <th>Opcao</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {(data.ivr || []).length ? data.ivr.map((row) => (
                <tr key={`${row.ivr}-${row.option}`}>
                  <td className="font-mono">{row.ivr}</td>
                  <td className="font-mono">{row.option}</td>
                  <td>{row.total}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="3" className="text-center text-[color:var(--text-muted)]">Sem navegacao de URA no periodo.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sbc-card overflow-hidden">
        <div className="p-4 border-b border-[color:var(--border-default)]">
          <h2 className="text-sm font-semibold">Historico detalhado de chamadas</h2>
        </div>
        <table className="sbc-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Origem</th>
              <th>Destino</th>
              <th>Fila</th>
              <th>Agente</th>
              <th>CID / DID</th>
              <th>Tipo</th>
              <th>Resultado</th>
              <th>Encerramento</th>
              <th>Espera</th>
              <th>Falado</th>
              <th>Gravacao</th>
            </tr>
          </thead>
          <tbody>
            {(data.calls || []).length ? data.calls.map((row) => (
              <tr key={`${row.call_id}-${row.started_at}`}>
                <td>{fmtDate(row.started_at)}</td>
                <td className="font-mono">{row.src || "-"}</td>
                <td className="font-mono">{row.dst || "-"}</td>
                <td>{row.queue_name || row.queue_number || "-"}</td>
                <td>{row.agent || "-"}</td>
                <td>
                  <div className="font-mono">{row.identity_number || "-"}</div>
                  <div className="text-[10px] text-[color:var(--text-muted)]">{row.identity_type || "-"}</div>
                </td>
                <td>{renderDirectionLabel(row.direction)}</td>
                <td>{row.disposition || "-"}</td>
                <td>{translateHangupCause(row.hangup_cause)}</td>
                <td>{fmtDuration(row.wait_sec || 0)}</td>
                <td>{fmtDuration(row.billsec || 0)}</td>
                <td>{row.recording_path ? <RecordingActions row={row} /> : "-"}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="12" className="text-center text-[color:var(--text-muted)]">Sem chamadas para os filtros informados.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>}

      {section === "received" && <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
          <MetricCard label="Recebidas" value={data.received_summary?.total || 0} detail="entradas externas" icon={PhoneCall} tone="blue" />
          <MetricCard label="Atendidas" value={data.received_summary?.answered || 0} detail="com audio estabelecido" icon={Headphones} tone="green" />
          <MetricCard label="Perdidas" value={data.received_summary?.missed || 0} detail="sem atendimento" icon={AlertTriangle} tone="red" />
          <MetricCard label="TME" value={fmtDuration(data.received_summary?.avg_wait_sec || 0)} detail="espera media" icon={Clock3} tone="amber" />
          <MetricCard label="TMA" value={fmtDuration(data.received_summary?.avg_talk_sec || 0)} detail="conversa media" icon={Users} tone="green" />
          <MetricCard label="Maior espera" value={fmtDuration(data.received_summary?.max_wait_sec || 0)} detail="pico no periodo" icon={BarChart3} tone="blue" />
        </div>
        <SectionCard title="Chamadas recebidas">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Origem</th>
                <th>Destino</th>
                <th>DID</th>
                <th>Fila</th>
                <th>Agente</th>
                <th>Espera</th>
                <th>Falado</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {(data.received_calls || []).length ? data.received_calls.map((row) => (
                <tr key={`${row.call_id}-${row.started_at}`}>
                  <td>{fmtDate(row.started_at)}</td>
                  <td className="font-mono">{row.src || "-"}</td>
                  <td className="font-mono">{row.dst || "-"}</td>
                  <td className="font-mono">{row.did || row.identity_number || "-"}</td>
                  <td>{row.queue_name || row.queue_number || "-"}</td>
                  <td>{row.agent || "-"}</td>
                  <td>{fmtDuration(row.wait_sec || 0)}</td>
                  <td>{fmtDuration(row.billsec || 0)}</td>
                  <td>{row.disposition || "-"}</td>
                </tr>
              )) : <tr><td colSpan="9" className="text-center text-[color:var(--text-muted)]">Nenhuma chamada recebida no periodo.</td></tr>}
            </tbody>
          </table>
        </SectionCard>
      </div>}

      {section === "placed" && <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
          <MetricCard label="Realizadas" value={data.placed_summary?.total || 0} detail="saidas externas" icon={PhoneCall} tone="blue" />
          <MetricCard label="Atendidas" value={data.placed_summary?.answered || 0} detail="com audio estabelecido" icon={Headphones} tone="green" />
          <MetricCard label="Ocupado" value={data.placed_summary?.busy || 0} detail="destino ocupado" icon={AlertTriangle} tone="amber" />
          <MetricCard label="Sem resposta" value={data.placed_summary?.not_answered || 0} detail="so chamou" icon={Clock3} tone="red" />
          <MetricCard label="Falhas" value={data.placed_summary?.failed || 0} detail="erro de entrega" icon={BarChart3} tone="red" />
          <MetricCard label="Tempo falado" value={fmtDuration(data.placed_summary?.total_talk_sec || 0)} detail="total do periodo" icon={Users} tone="green" />
        </div>
        <SectionCard title="Chamadas realizadas">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Ramal / Origem</th>
                <th>Numero chamado</th>
                <th>Fila</th>
                <th>Agente</th>
                <th>CID</th>
                <th>Tronco</th>
                <th>Falado</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {(data.placed_calls || []).length ? data.placed_calls.map((row) => (
                <tr key={`${row.call_id}-${row.started_at}`}>
                  <td>{fmtDate(row.started_at)}</td>
                  <td className="font-mono">{row.src || "-"}</td>
                  <td className="font-mono">{row.dst || "-"}</td>
                  <td>{row.queue_name || row.queue_number || "-"}</td>
                  <td>{row.agent || "-"}</td>
                  <td className="font-mono">{row.identity_number || "-"}</td>
                  <td>{row.trunk_name || "-"}</td>
                  <td>{fmtDuration(row.billsec || 0)}</td>
                  <td>{row.disposition || "-"}</td>
                </tr>
              )) : <tr><td colSpan="9" className="text-center text-[color:var(--text-muted)]">Nenhuma chamada realizada no periodo.</td></tr>}
            </tbody>
          </table>
        </SectionCard>
      </div>}

      {section === "missed" && <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          <MetricCard label="Perdidas" value={data.direction_summary?.missed || 0} detail="nao atendidas" icon={AlertTriangle} tone="red" />
          <MetricCard label="Recebidas" value={data.direction_summary?.received || 0} detail="base de entrada" icon={PhoneCall} tone="blue" />
          <MetricCard label="Realizadas" value={data.direction_summary?.placed || 0} detail="base de saida" icon={BarChart3} tone="amber" />
          <MetricCard label="Internas" value={data.direction_summary?.internal || 0} detail="entre ramais" icon={Users} tone="green" />
        </div>
        <SectionCard title="Chamadas perdidas e nao atendidas" helper="Lista consolidada das chamadas sem resposta no periodo filtrado.">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Origem</th>
                <th>Destino</th>
                <th>Fila</th>
                <th>Agente</th>
                <th>Tipo</th>
                <th>Espera</th>
                <th>Encerramento</th>
              </tr>
            </thead>
            <tbody>
              {(data.missed_calls || []).length ? data.missed_calls.map((row) => (
                <tr key={`${row.call_id}-${row.started_at}`}>
                  <td>{fmtDate(row.started_at)}</td>
                  <td className="font-mono">{row.src || "-"}</td>
                  <td className="font-mono">{row.dst || "-"}</td>
                  <td>{row.queue_name || row.queue_number || "-"}</td>
                  <td>{row.agent || "-"}</td>
                  <td>{renderDirectionLabel(row.direction)}</td>
                  <td>{fmtDuration(row.wait_sec || 0)}</td>
                  <td>{translateHangupCause(row.hangup_cause)}</td>
                </tr>
              )) : <tr><td colSpan="8" className="text-center text-[color:var(--text-muted)]">Nenhuma chamada perdida no periodo.</td></tr>}
            </tbody>
          </table>
        </SectionCard>
      </div>}

      {section === "abandoned" && <div className="space-y-4">
        <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-4">
          <SectionCard title="Faixas de abandono">
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              {(data.abandonment_ranges || []).map((row) => (
                <div key={row.range} className="rounded-sm border border-[color:var(--border-default)] p-3">
                  <div className="text-[10px] uppercase text-[color:var(--text-muted)]">{row.range}</div>
                  <div className="text-xl font-mono mt-1">{row.total || 0}</div>
                </div>
              ))}
            </div>
          </SectionCard>
          <SectionCard title="Motivos de abandono">
            <div className="p-4 grid grid-cols-1 gap-2">
              {(data.abandonment_causes || []).length ? data.abandonment_causes.map((cause) => (
                <CausePill key={cause.name} label={translateAbandonmentCause(cause.name)} value={cause.value} tone="amber" />
              )) : <div className="text-sm text-[color:var(--text-muted)]">Sem abandonos no periodo.</div>}
            </div>
          </SectionCard>
        </div>
        <SectionCard title="Chamadas abandonadas">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Telefone</th>
                <th>Fila</th>
                <th>Agente</th>
                <th>Espera</th>
                <th>Resultado</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              {(data.abandoned_calls || []).length ? data.abandoned_calls.map((row) => (
                <tr key={`${row.call_id}-${row.started_at}`}>
                  <td>{fmtDate(row.started_at)}</td>
                  <td className="font-mono">{row.src || "-"}</td>
                  <td>{row.queue_name || row.queue_number || "-"}</td>
                  <td>{row.agent || "-"}</td>
                  <td>{fmtDuration(row.wait_sec || 0)}</td>
                  <td>{row.disposition || "-"}</td>
                  <td>{translateHangupCause(row.hangup_cause)}</td>
                </tr>
              )) : <tr><td colSpan="7" className="text-center text-[color:var(--text-muted)]">Nenhuma chamada abandonada no periodo.</td></tr>}
            </tbody>
          </table>
        </SectionCard>
      </div>}

      {section === "ivr" && <div className="sbc-card overflow-hidden">
        <div className="p-4 border-b border-[color:var(--border-default)]">
          <h2 className="text-sm font-semibold">URA / navegacao</h2>
        </div>
        <table className="sbc-table">
          <thead>
            <tr>
              <th>URA</th>
              <th>Opcao</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {(data.ivr || []).length ? data.ivr.map((row) => (
              <tr key={`${row.ivr}-${row.option}`}>
                <td className="font-mono">{row.ivr}</td>
                <td className="font-mono">{row.option}</td>
                <td>{row.total}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="3" className="text-center text-[color:var(--text-muted)]">Sem navegacao de URA no periodo.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>}

      {section === "recordings" && <div className="sbc-card overflow-hidden">
        <div className="space-y-4">
          <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-4">
            <SectionCard
              title={filters.agent ? `Gravacoes do agente ${filters.agent}` : "Gravacoes por agente"}
              helper={filters.agent ? "Exibindo as gravacoes relacionadas ao agente filtrado." : "Resumo por agente logado para localizar rapidamente quem gerou cada gravacao."}
            >
              <table className="sbc-table">
                <thead>
                  <tr>
                    <th>Agente</th>
                    <th>Ramal</th>
                    <th>Gravacoes</th>
                    <th>Tempo gravado</th>
                    <th>Ultima gravacao</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recording_agents || []).length ? data.recording_agents.map((row) => (
                    <tr key={`${row.agent}-${row.active_extension}`}>
                      <td>{row.agent || "-"}</td>
                      <td className="font-mono">{row.active_extension || "-"}</td>
                      <td>{row.total || 0}</td>
                      <td>{fmtDuration(row.talk_sec || 0)}</td>
                      <td>{fmtDate(row.latest_at)}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="text-center text-[color:var(--text-muted)]">Nenhuma gravacao por agente no periodo.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </SectionCard>

            <SectionCard
              title={filters.agent ? "Lista das gravacoes do agente" : "Gravacoes recentes"}
              helper="Cada item mostra a gravação já associada ao agente que estava logado na chamada."
            >
              <table className="sbc-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Agente</th>
                    <th>Ramal</th>
                    <th>Origem</th>
                    <th>Destino</th>
                    <th>Fila</th>
                    <th>Duracao</th>
                    <th>Arquivo</th>
                    <th>Audio</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recordings || []).length ? data.recordings.map((row) => (
                    <tr key={`${row.call_id}-${row.recording_path}`}>
                      <td>{fmtDate(row.started_at)}</td>
                      <td>{row.agent_label || row.agent || "-"}</td>
                      <td className="font-mono">{row.active_extension || "-"}</td>
                      <td className="font-mono">{row.src || "-"}</td>
                      <td className="font-mono">{row.dst || "-"}</td>
                      <td>{row.queue_name || row.queue_number || "-"}</td>
                      <td>{fmtDuration(row.billsec || row.duration || 0)}</td>
                      <td className="font-mono">{String(row.recording_path || "").split("/").pop() || "-"}</td>
                      <td>{row.recording_path ? <RecordingActions row={row} /> : "-"}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="9" className="text-center text-[color:var(--text-muted)]">Nenhuma gravacao encontrada no periodo.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </SectionCard>
          </div>
        </div>
      </div>}
    </div>
  );
}
